import { useId } from 'react';
import type { AssistantToolInfo } from '@cumpli/shared';
import { cn } from '../ui';
import { toolIcon } from './tool-icons';

/**
 * The discovery surface on the /agente empty state: the RBAC-filtered capability
 * catalog rendered as cards, grouped into "Consultas" (read) and "Acciones"
 * (write). Each card seeds the composer with the tool's `prompt` (never
 * auto-sends), so the agent then drives the normal flow — and for the write
 * ('accion') tools that means the propose → confirm ActionCard flow.
 *
 * Rendered ONLY when the active conversation is empty; the "/" menu (slash-menu)
 * is the anytime counterpart. Cards reflow grid → single column on mobile.
 */

/** One capability card — icon + label + one-line description; seeds the composer. */
function ToolCard({
  tool,
  onPick,
}: {
  tool: AssistantToolInfo;
  onPick: (prompt: string) => void;
}) {
  const Icon = toolIcon(tool.name);
  return (
    <button
      type="button"
      onClick={() => onPick(tool.prompt)}
      className="group flex items-start gap-3 rounded-2xl border border-line bg-panel p-3.5 text-left transition-all hover:border-primary/40 hover:shadow-[0_10px_30px_-18px_rgba(138,46,60,.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/[0.06] text-primary transition-colors group-hover:bg-primary/10">
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </span>
      <span className="min-w-0">
        <span className="block font-serif text-sm font-medium leading-snug text-ink">
          {tool.label}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-ink-soft">
          {tool.description}
        </span>
      </span>
    </button>
  );
}

/** A labeled group of capability cards; renders nothing when the group is empty. */
function ToolSection({
  title,
  tools,
  onPick,
}: {
  title: string;
  tools: AssistantToolInfo[];
  onPick: (prompt: string) => void;
}) {
  const headingId = useId();
  if (tools.length === 0) return null;
  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2.5 flex items-center gap-2 pl-0.5">
        <span
          id={headingId}
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-faint"
        >
          {title}
        </span>
        <span className="grid h-4 min-w-4 place-items-center rounded-full bg-primary/10 px-1 font-mono text-[9px] font-bold text-primary-dark">
          {tools.length}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
        {tools.map((tool) => (
          <ToolCard key={tool.name} tool={tool} onPick={onPick} />
        ))}
      </div>
    </section>
  );
}

export function ToolExplorer({
  tools,
  onPick,
  className,
}: {
  tools: readonly AssistantToolInfo[];
  /** Seed the composer with the picked capability's prompt (no auto-send). */
  onPick: (prompt: string) => void;
  className?: string | undefined;
}) {
  const consultas = tools.filter((t) => t.category === 'consulta');
  const acciones = tools.filter((t) => t.category === 'accion');
  return (
    <div className={cn('space-y-6', className)}>
      <ToolSection title="Consultas" tools={consultas} onPick={onPick} />
      <ToolSection title="Acciones" tools={acciones} onPick={onPick} />
    </div>
  );
}
